import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Skeleton,
  useTheme,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
  Collapse,
  IconButton,
  Typography,
  LinearProgress,
  alpha,
} from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { getCompanyAnalytics } from "../../api/analytics";
import { getTrips } from "../../api/trips";
import { getPackages } from "../../api/packages";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Cancel,
  Refresh,
  Add,
  ExpandMore,
  ExpandLess,
  Route,
  Today,
  DepartureBoard,
  Payments,
  LocalShipping,
  ErrorOutline,
  Schedule,
  PersonOff,
  Replay,
  Assignment,
  ArrowForward,
} from "@mui/icons-material";
import dayjs from "dayjs";
import "dayjs/locale/uk";
import "dayjs/locale/en";
import i18n from "../../i18n";

const formatMoney = (value) => {
  const amount = Number(value || 0).toLocaleString("uk-UA", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  return i18n.t("units.uahAmount", { amount });
};

const formatEta = (departure, t) => {
  const mins = dayjs(departure).diff(dayjs(), "minute");
  if (mins <= 0) return t("admin.tripSoon");
  const hours = Math.floor(mins / 60);
  const minutes = mins % 60;
  return t("admin.tripEta", {
    time: `${hours}:${String(minutes).padStart(2, "0")}`,
  });
};

const KpiCard = ({ title, icon: Icon, value, subtitle, extra, extraColor, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2.25,
      height: "100%",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      cursor: onClick ? "pointer" : "default",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} color="text.secondary">
      <Icon sx={{ fontSize: 18 }} />
      <Typography variant="overline" fontWeight={600} letterSpacing={0.6}>
        {title}
      </Typography>
    </Stack>
    <Typography variant="h4" fontWeight={700} sx={{ mt: 1.25 }}>
      {value}
    </Typography>
    {subtitle ? (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {subtitle}
      </Typography>
    ) : null}
    {extra ? (
      <Typography variant="body2" fontWeight={600} sx={{ mt: 0.75, color: extraColor || "text.primary" }}>
        {extra}
      </Typography>
    ) : null}
  </Paper>
);

const DashboardPage = () => {
  const { t, i18n } = useTranslation("common");
  const [data, setData] = useState(null);
  const [todayTrips, setTodayTrips] = useState({ count: 0, nearest: null, cards: [] });
  const [packagesCount, setPackagesCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const theme = useTheme();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const now = dayjs();
      const today = now.format("YYYY-MM-DD");
      const [analytics, todayList, upcomingList, packagesResp] = await Promise.allSettled([
        getCompanyAnalytics(),
        getTrips({ date_from: now.toISOString(), date_to: today, page_size: 1 }),
        getTrips({ date_from: now.toISOString(), page_size: 3 }),
        getPackages(1, 1),
      ]);
      if (analytics.status === "fulfilled") {
        setData(analytics.value);
      } else {
        setData(null);
      }
      const todayValue = todayList.status === "fulfilled" ? todayList.value : null;
      const upcomingValue = upcomingList.status === "fulfilled" ? upcomingList.value : null;
      const cards = upcomingValue?.results || [];
      setTodayTrips({
        count: todayValue?.count ?? 0,
        nearest: cards[0] || null,
        cards,
      });
      if (packagesResp.status === "fulfilled") {
        setPackagesCount(packagesResp.value?.data?.count ?? 0);
      } else {
        setPackagesCount(null);
      }
      setLastUpdated(dayjs());
    } catch (error) {
      console.error("Failed to load analytics", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton width={280} height={40} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rounded" height={140} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography color="text.secondary" gutterBottom>
          {t("admin.dashboardLoadError")}
        </Typography>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchData}>
          {t("admin.refresh")}
        </Button>
      </Box>
    );
  }

  const unpaidCount = (data.status_breakdown || [])
    .filter((s) => ["not_paid", "processing", "failed", "pending"].includes(s.status))
    .reduce((sum, s) => sum + (s.count || 0), 0);

  const cancelledCount = data.cancelled_count || 0;
  const noShowCount = data.no_show_count || 0;
  const refundingCount = data.refunding_count || 0;
  const hasAlerts =
    cancelledCount > 0 || noShowCount > 0 || unpaidCount > 0 || refundingCount > 0;

  const bookingsChartData =
    data.bookings_per_day?.map((item) => ({
      ...item,
      day: dayjs(item.day).format("DD.MM"),
    })) || [];

  const routesData =
    data.top_routes?.map((item) => ({
      name: item.trip__route__name || t("admin.unknownRoute"),
      bookings: item.count,
    })) || [];

  const today = dayjs().format("YYYY-MM-DD");
  const dateLabel = dayjs()
    .locale(i18n.language === "en" ? "en" : "uk")
    .format("D MMMM");

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, pb: 5, minWidth: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "flex-end",
          gap: 2,
          mb: 2.5,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            {t("admin.dashboardTodayTitle", { date: dateLabel })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {lastUpdated
              ? t("admin.dashboardUpdated", { time: lastUpdated.format("HH:mm") })
              : t("admin.dashboardScope")}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData}>
            {t("admin.refresh")}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/administrator/bookings/new")}
          >
            {t("admin.newBooking")}
          </Button>
        </Stack>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title={t("admin.today")}
            icon={Today}
            value={data.today?.bookings ?? 0}
            subtitle={t("admin.kpiTodayDetail", {
              passengers: data.today?.passengers ?? 0,
            })}
            extra={formatMoney(data.today?.revenue)}
            onClick={() =>
              navigate(`/administrator/bookings?created_from=${today}&created_to=${today}`)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title={t("admin.kpiUpcomingTrips")}
            icon={DepartureBoard}
            value={todayTrips.count}
            subtitle={t("admin.kpiUpcomingUntilEod")}
            extra={
              todayTrips.nearest
                ? t("admin.kpiNearest", {
                    when: formatEta(todayTrips.nearest.departure_date, t),
                  })
                : t("admin.noUpcomingTrips")
            }
            extraColor={todayTrips.nearest ? "warning.main" : "text.secondary"}
            onClick={() => navigate("/administrator/trips/passengers")}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title={t("admin.kpiRevenue30")}
            icon={Payments}
            value={formatMoney(data.total_paid)}
            subtitle={t("admin.incomeSubtitle")}
            onClick={() =>
              navigate(`/administrator/bookings?status=${encodeURIComponent("paid")}`)
            }
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <KpiCard
            title={t("admin.kpiPackages")}
            icon={LocalShipping}
            value={packagesCount ?? "—"}
            subtitle={t("admin.kpiPackagesSubtitle")}
            onClick={() => navigate("/administrator/packages")}
          />
        </Grid>
      </Grid>

      {hasAlerts ? (
        <Paper
          elevation={0}
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            borderLeft: "4px solid",
            borderLeftColor: "warning.main",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <ErrorOutline color="warning" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t("admin.needsAttention")}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {unpaidCount > 0 ? (
              <Chip
                icon={<Schedule />}
                label={t("admin.attentionUnpaid", { count: unpaidCount })}
                color="warning"
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/administrator/bookings?status=${encodeURIComponent(
                      "pending,processing,not_paid"
                    )}`
                  )
                }
              />
            ) : null}
            {refundingCount > 0 ? (
              <Chip
                icon={<Replay />}
                label={t("admin.attentionRefund", { count: refundingCount })}
                color="warning"
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/administrator/bookings?status=${encodeURIComponent("refunding")}`
                  )
                }
              />
            ) : null}
            {cancelledCount > 0 ? (
              <Chip
                icon={<Cancel />}
                label={t("admin.attentionCancelled", { count: cancelledCount })}
                color="error"
                variant="outlined"
                onClick={() =>
                  navigate(`/administrator/bookings?status=${encodeURIComponent("cancelled")}`)
                }
              />
            ) : null}
            {noShowCount > 0 ? (
              <Chip
                icon={<PersonOff />}
                label={t("admin.attentionNoShow", { count: noShowCount })}
                variant="outlined"
                onClick={() =>
                  navigate(`/administrator/bookings?status=${encodeURIComponent("no_show")}`)
                }
              />
            ) : null}
          </Stack>
        </Paper>
      ) : null}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          mb: 1.5,
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {t("admin.kpiUpcomingTrips")}
        </Typography>
        <Button
          size="small"
          endIcon={<ArrowForward />}
          onClick={() => navigate("/administrator/trips/passengers")}
        >
          {t("admin.allTrips")}
        </Button>
      </Box>

      {todayTrips.cards.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ p: 3, mb: 2.5, borderRadius: 3, border: "1px solid", borderColor: "divider" }}
        >
          <Typography color="text.secondary">{t("admin.noUpcomingTrips")}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {todayTrips.cards.map((trip) => {
            const capacity = Number(trip.bus?.total_seats || 0);
            const free = Number(trip.free_seats || 0);
            const sold = Math.max(0, capacity - free);
            const pct = capacity > 0 ? Math.round((100 * sold) / capacity) : 0;
            const pkg = Number(trip.packages_count || 0);
            return (
              <Grid item xs={12} md={4} key={trip.id}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, height: "100%", borderRadius: 3, border: "1px solid", borderColor: "divider" }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    spacing={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={600}
                      noWrap
                      sx={{ minWidth: 0, flex: 1 }}
                    >
                      {trip.route?.name || t("admin.unknownRoute")}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      useFlexGap
                      flexWrap="wrap"
                      justifyContent="flex-end"
                      sx={{ flexShrink: 0, maxWidth: "58%" }}
                    >
                      <Chip
                        size="small"
                        label={formatEta(trip.departure_date, t)}
                        color="warning"
                        variant="outlined"
                      />
                      {trip.sold_out ? (
                        <Chip
                          size="small"
                          label={t("admin.tripSoldOut")}
                          color="default"
                          variant="outlined"
                        />
                      ) : null}
                    </Stack>
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {dayjs(trip.departure_date).format("DD.MM.YYYY · HH:mm")}
                    {trip.bus?.name ? ` · ${trip.bus.name}` : ""}
                    {trip.bus?.number ? ` ${trip.bus.number}` : ""}
                  </Typography>
                  <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mt: 1.75 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t("admin.seatsOccupancy", { sold, capacity })}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pct}%
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, pct)}
                    sx={{
                      mt: 0.75,
                      height: 6,
                      borderRadius: 1,
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    }}
                  />
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 1.75 }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" color="text.secondary">
                      <LocalShipping sx={{ fontSize: 18 }} />
                      <Typography variant="body2">
                        {t("admin.packagesOnTrip", { count: pkg })}
                      </Typography>
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Assignment />}
                      onClick={() => navigate(`/administrator/trips/${trip.id}/passengers`)}
                    >
                      {t("admin.manifest")}
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.5,
            cursor: "pointer",
          }}
          onClick={() => setAnalyticsOpen((v) => !v)}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            {t("admin.analyticsSection")}
          </Typography>
          <IconButton size="small" aria-label={t("admin.analyticsSection")}>
            {analyticsOpen ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
        <Collapse in={analyticsOpen}>
          <Box sx={{ px: 2, pb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={7}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t("admin.bookingsLast30Days")}
                </Typography>
                {bookingsChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <AreaChart data={bookingsChartData}>
                      <defs>
                        <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                          <stop
                            offset="5%"
                            stopColor={theme.palette.primary.main}
                            stopOpacity={0.3}
                          />
                          <stop
                            offset="95%"
                            stopColor={theme.palette.primary.main}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={alpha(theme.palette.divider, 0.8)}
                        vertical={false}
                      />
                      <XAxis dataKey="day" tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                      <YAxis
                        allowDecimals={false}
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: "1px solid",
                          borderColor: theme.palette.divider,
                        }}
                        formatter={(value) => [value, t("admin.bookingsCount")]}
                      />
                      <Area
                        type="monotone"
                        dataKey="count"
                        stroke={theme.palette.primary.main}
                        strokeWidth={2}
                        fill="url(#colorCount)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <Box py={6} textAlign="center">
                    <Typography color="text.secondary">{t("admin.noAnalyticsData")}</Typography>
                  </Box>
                )}
              </Grid>
              <Grid item xs={12} lg={5}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t("admin.topRoutes")}
                </Typography>
                {routesData.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t("admin.route")}</TableCell>
                          <TableCell align="right">{t("admin.bookingsCount")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {routesData.map((row) => (
                          <TableRow key={row.name} hover>
                            <TableCell>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Route sx={{ fontSize: 18, color: "text.secondary" }} />
                                {row.name}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>{row.bookings}</Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Box py={4} textAlign="center">
                    <Typography color="text.secondary">{t("admin.noAnalyticsData")}</Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default DashboardPage;
